/**
 * TanStack Query configuration.
 *
 * The important policy here is retry: GETs retry, MUTATIONS NEVER DO. The backend
 * has no idempotency keys, so silently replaying a reassign or a publish could
 * apply it twice.
 */

import { useState, type ReactNode } from 'react'
import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import { isApiError } from '@/services/api/errors'

/** Failures that will never succeed on a retry. */
const TERMINAL_KINDS = new Set([
  'unauthenticated',
  'forbidden',
  'not_found',
  'validation',
  'conflict',
  'gone',
  'cancelled',
])

export function createQueryClient(): QueryClient {
  return new QueryClient({
    defaultOptions: {
      queries: {
        staleTime: 30_000,
        gcTime: 5 * 60_000,
        refetchOnWindowFocus: false,
        // Operational data should be current when the user comes back to the tab,
        // but refetching on every focus makes a busy dashboard flicker.
        refetchOnReconnect: true,
        retry: (failureCount, error) => {
          if (isApiError(error) && TERMINAL_KINDS.has(error.kind)) return false
          return failureCount < 2
        },
        retryDelay: (attempt) => Math.min(1000 * 2 ** attempt, 8000),
      },
      mutations: {
        retry: false,
      },
    },
  })
}

export function QueryProvider({ children }: { children: ReactNode }) {
  const [client] = useState(createQueryClient)
  return <QueryClientProvider client={client}>{children}</QueryClientProvider>
}
